import { useEffect, useState } from 'react'

// Dev-facing visual styleguide — renders every design token from index.css
// so we can eyeball drift between the spec and what's actually shipping.
// Mounted at #/styleguide, outside LoginGate.

type Token = { name: string; cssVar: string }

const SURFACES: Token[] = [
  { name: 'surface-0 (page)', cssVar: '--surface-0' },
  { name: 'surface-1 (cards)', cssVar: '--surface-1' },
  { name: 'surface-2 (recessed)', cssVar: '--surface-2' },
  { name: 'surface-3 (deeper accent)', cssVar: '--surface-3' },
  { name: 'surface-4 (warm hairline)', cssVar: '--surface-4' },
]

const INK: Token[] = [
  { name: 'ink-1 (headlines)', cssVar: '--ink-1' },
  { name: 'ink-2 (labels, secondary)', cssVar: '--ink-2' },
  { name: 'ink-3 (hints, microcopy)', cssVar: '--ink-3' },
  { name: 'ink-4 (disabled, ghost)', cssVar: '--ink-4' },
  { name: 'ink-5 (dividers in copy)', cssVar: '--ink-5' },
]

const ACCENT: Token[] = [
  { name: 'accent', cssVar: '--accent' },
  { name: 'accent-hover', cssVar: '--accent-hover' },
  { name: 'accent-deep', cssVar: '--accent-deep' },
  { name: 'accent-muted', cssVar: '--accent-muted' },
  { name: 'accent-subtle', cssVar: '--accent-subtle' },
  { name: 'accent-glow', cssVar: '--accent-glow' },
  { name: 'text-on-accent', cssVar: '--text-on-accent' },
  { name: 'recording', cssVar: '--recording' },
  { name: 'recording-hover', cssVar: '--recording-hover' },
  { name: 'recording-glow', cssVar: '--recording-glow' },
  { name: 'recording-muted', cssVar: '--recording-muted' },
  { name: 'soothe-glow', cssVar: '--soothe-glow' },
]

const SEMANTIC: Token[] = [
  { name: 'success', cssVar: '--success' },
  { name: 'warning', cssVar: '--warning' },
  { name: 'error', cssVar: '--error' },
  { name: 'info', cssVar: '--info' },
]

const GOLD: Token[] = [
  { name: 'gold', cssVar: '--gold' },
  { name: 'gold-soft', cssVar: '--gold-soft' },
]

const BORDERS: Token[] = [
  { name: 'border-subtle', cssVar: '--border-subtle' },
  { name: 'border-default', cssVar: '--border-default' },
  { name: 'border-accent', cssVar: '--border-accent' },
  { name: 'border-hairline', cssVar: '--border-hairline' },
  { name: 'glass-bg', cssVar: '--glass-bg' },
  { name: 'glass-border', cssVar: '--glass-border' },
]

const FONT_FAMILIES: Token[] = [
  { name: 'font-sans', cssVar: '--font-sans' },
  { name: 'font-serif', cssVar: '--font-serif' },
  { name: 'font-mono', cssVar: '--font-mono' },
]

const TYPE_SCALE: Token[] = [
  { name: 'text-display', cssVar: '--text-display' },
  { name: 'text-h1', cssVar: '--text-h1' },
  { name: 'text-h2', cssVar: '--text-h2' },
  { name: 'text-h3', cssVar: '--text-h3' },
  { name: 'text-body-lg', cssVar: '--text-body-lg' },
  { name: 'text-body', cssVar: '--text-body' },
  { name: 'text-small', cssVar: '--text-small' },
  { name: 'text-caption', cssVar: '--text-caption' },
  { name: 'text-eyebrow', cssVar: '--text-eyebrow' },
]

const SPACING: Token[] = [
  { name: 'space-2xs', cssVar: '--space-2xs' },
  { name: 'space-xs', cssVar: '--space-xs' },
  { name: 'space-sm', cssVar: '--space-sm' },
  { name: 'space-md', cssVar: '--space-md' },
  { name: 'space-lg', cssVar: '--space-lg' },
  { name: 'space-xl', cssVar: '--space-xl' },
  { name: 'space-2xl', cssVar: '--space-2xl' },
  { name: 'space-3xl', cssVar: '--space-3xl' },
]

const RADII: Token[] = [
  { name: 'radius-sm', cssVar: '--radius-sm' },
  { name: 'radius-md', cssVar: '--radius-md' },
  { name: 'radius-lg', cssVar: '--radius-lg' },
  { name: 'radius-xl', cssVar: '--radius-xl' },
  { name: 'radius-pill', cssVar: '--radius-pill' },
]

const SAMPLE_TEXT = 'The quick brown fox jumps over the lazy dog. 0123456789'

// Resolve a CSS custom property's computed value off the document root.
// Re-evaluated once on mount; tokens are static for our purposes.
function useResolvedTokens(tokens: Token[]): Record<string, string> {
  const [resolved, setResolved] = useState<Record<string, string>>({})
  useEffect(() => {
    const styles = getComputedStyle(document.documentElement)
    const next: Record<string, string> = {}
    for (const t of tokens) {
      next[t.cssVar] = styles.getPropertyValue(t.cssVar).trim()
    }
    setResolved(next)
  }, [tokens])
  return resolved
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section
      className="py-10"
      style={{ borderTop: '1px solid var(--border-subtle)' }}
    >
      <h2
        className="mb-6 uppercase tracking-widest"
        style={{
          color: 'var(--ink-3)',
          fontSize: 'var(--text-eyebrow)',
          fontFamily: 'var(--font-sans)',
          fontWeight: 600,
        }}
      >
        {title}
      </h2>
      {children}
    </section>
  )
}

function Swatch({ name, cssVar, value }: { name: string; cssVar: string; value: string }) {
  return (
    <div className="flex items-center gap-4">
      <div
        className="shrink-0"
        style={{
          width: 64,
          height: 64,
          background: `var(${cssVar})`,
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-sm)',
        }}
      />
      <div className="flex flex-col gap-0.5 min-w-0">
        <code
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--text-small)',
            color: 'var(--ink-1)',
          }}
        >
          {name}
        </code>
        <code
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--text-caption)',
            color: 'var(--ink-3)',
          }}
        >
          {value || '—'}
        </code>
      </div>
    </div>
  )
}

function ColorGroup({ title, tokens }: { title: string; tokens: Token[] }) {
  const resolved = useResolvedTokens(tokens)
  return (
    <div className="mb-8">
      <h3
        className="mb-4"
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-body)',
          color: 'var(--ink-2)',
          fontWeight: 600,
        }}
      >
        {title}
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {tokens.map((t) => (
          <Swatch key={t.cssVar} name={t.name} cssVar={t.cssVar} value={resolved[t.cssVar] ?? ''} />
        ))}
      </div>
    </div>
  )
}

function FontFamiliesBlock() {
  const resolved = useResolvedTokens(FONT_FAMILIES)
  return (
    <div className="flex flex-col gap-8">
      {FONT_FAMILIES.map((t) => (
        <div key={t.cssVar} className="flex flex-col gap-2">
          <div className="flex items-baseline gap-3 flex-wrap">
            <code
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--text-small)',
                color: 'var(--ink-1)',
              }}
            >
              {t.name}
            </code>
            <code
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--text-caption)',
                color: 'var(--ink-3)',
              }}
            >
              {resolved[t.cssVar] ?? ''}
            </code>
          </div>
          <p
            style={{
              fontFamily: `var(${t.cssVar})`,
              fontSize: 'var(--text-h3)',
              color: 'var(--ink-1)',
              margin: 0,
            }}
          >
            {SAMPLE_TEXT}
          </p>
        </div>
      ))}
    </div>
  )
}

function TypeScaleBlock() {
  const resolved = useResolvedTokens(TYPE_SCALE)
  return (
    <div className="flex flex-col gap-6">
      {TYPE_SCALE.map((t) => {
        const raw = resolved[t.cssVar] ?? ''
        // Convert rem to px for display when applicable. Tokens are rem;
        // 1rem = 16px assuming default root size (this app does not change it).
        let px = ''
        const m = raw.match(/^([\d.]+)rem$/)
        if (m) px = `${parseFloat(m[1]) * 16}px`
        else if (raw.endsWith('px')) px = raw
        return (
          <div key={t.cssVar} className="flex flex-col gap-1">
            <div className="flex items-baseline gap-3 flex-wrap">
              <code
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--text-small)',
                  color: 'var(--ink-1)',
                }}
              >
                {t.name}
              </code>
              <code
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--text-caption)',
                  color: 'var(--ink-3)',
                }}
              >
                {raw}
                {px && raw !== px ? ` (${px})` : ''}
              </code>
            </div>
            <span
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: `var(${t.cssVar})`,
                color: 'var(--ink-1)',
                lineHeight: 1.1,
              }}
            >
              Aa Bb 123
            </span>
          </div>
        )
      })}
    </div>
  )
}

function SpacingBlock() {
  const resolved = useResolvedTokens(SPACING)
  return (
    <div className="flex flex-col gap-4">
      {SPACING.map((t) => {
        const raw = resolved[t.cssVar] ?? ''
        let px = ''
        const m = raw.match(/^([\d.]+)rem$/)
        if (m) px = `${parseFloat(m[1]) * 16}px`
        return (
          <div key={t.cssVar} className="flex items-center gap-4">
            <code
              className="shrink-0"
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--text-small)',
                color: 'var(--ink-1)',
                width: 130,
              }}
            >
              {t.name}
            </code>
            <div
              style={{
                width: `var(${t.cssVar})`,
                height: 14,
                background: 'var(--accent)',
                borderRadius: 2,
              }}
            />
            <code
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--text-caption)',
                color: 'var(--ink-3)',
              }}
            >
              {raw}
              {px ? ` (${px})` : ''}
            </code>
          </div>
        )
      })}
    </div>
  )
}

function RadiusBlock() {
  const resolved = useResolvedTokens(RADII)
  return (
    <div className="flex flex-wrap gap-8">
      {RADII.map((t) => (
        <div key={t.cssVar} className="flex flex-col items-center gap-2">
          <div
            style={{
              width: 64,
              height: 64,
              background: 'var(--accent-muted)',
              border: '1px solid var(--border-accent)',
              borderRadius: `var(${t.cssVar})`,
            }}
          />
          <code
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--text-small)',
              color: 'var(--ink-1)',
            }}
          >
            {t.name}
          </code>
          <code
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--text-caption)',
              color: 'var(--ink-3)',
            }}
          >
            {resolved[t.cssVar] ?? ''}
          </code>
        </div>
      ))}
    </div>
  )
}

export default function Styleguide() {
  return (
    <div
      className="w-full overflow-y-auto"
      style={{
        height: '100dvh',
        background: 'var(--surface-0)',
        color: 'var(--ink-1)',
        fontFamily: 'var(--font-sans)',
      }}
    >
      <div className="max-w-3xl mx-auto px-6 py-12">
        <header className="mb-6">
          <p
            className="uppercase tracking-widest mb-3"
            style={{
              color: 'var(--ink-3)',
              fontSize: 'var(--text-eyebrow)',
              fontWeight: 600,
            }}
          >
            The Lost Club / dev
          </p>
          <h1
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 'var(--text-h1)',
              color: 'var(--ink-1)',
              margin: 0,
              lineHeight: 1.05,
            }}
          >
            Styleguide
          </h1>
          <p
            className="mt-3"
            style={{
              color: 'var(--ink-2)',
              fontSize: 'var(--text-body)',
              maxWidth: '52ch',
            }}
          >
            Live render of every design token defined in{' '}
            <code style={{ fontFamily: 'var(--font-mono)' }}>src/index.css</code>. Values
            are resolved off <code style={{ fontFamily: 'var(--font-mono)' }}>:root</code>{' '}
            at runtime, so anything you see here is what the app actually ships.
          </p>
        </header>

        <Section title="Color — Surfaces">
          <ColorGroup title="Surfaces" tokens={SURFACES} />
        </Section>

        <Section title="Color — Ink">
          <ColorGroup title="Ink" tokens={INK} />
        </Section>

        <Section title="Color — Accent &amp; Recording">
          <ColorGroup title="Accent" tokens={ACCENT} />
        </Section>

        <Section title="Color — Semantic">
          <ColorGroup title="Semantic" tokens={SEMANTIC} />
        </Section>

        <Section title="Color — Gold">
          <ColorGroup title="Gold" tokens={GOLD} />
        </Section>

        <Section title="Color — Borders &amp; Glass">
          <ColorGroup title="Borders" tokens={BORDERS} />
        </Section>

        <Section title="Type families">
          <FontFamiliesBlock />
        </Section>

        <Section title="Type scale">
          <TypeScaleBlock />
        </Section>

        <Section title="Spacing">
          <SpacingBlock />
        </Section>

        <Section title="Border radius">
          <RadiusBlock />
        </Section>

        <div
          className="py-10 mt-2"
          style={{
            borderTop: '1px solid var(--border-subtle)',
            color: 'var(--ink-4)',
            fontSize: 'var(--text-caption)',
          }}
        >
          End of tokens.
        </div>
      </div>
    </div>
  )
}
